import React, { useMemo, useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography, Container, Alert, Snackbar } from '@mui/material';
import { motion } from 'framer-motion';
import EuropeMap from './components/Map/EuropeMap';
import KPICard from './components/KPIBoxes/KPICard';
import LastUpdated from './components/DataFreshness/LastUpdated';
import useSharedTicketData from './hooks/useSharedTicketData';
import { initializeAmplify } from './services/amplifyConfig';
import theme from './styles/theme';
import './App.css';
import dayjs from 'dayjs';

// Create Material-UI theme from our custom theme
const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: theme.colors.primary[500],
      light: theme.colors.primary[300],
      dark: theme.colors.primary[700],
    },
    secondary: {
      main: theme.colors.secondary[500],
      light: theme.colors.secondary[300],
      dark: theme.colors.secondary[700],
    },
    background: {
      default: theme.colors.background.primary,
      paper: theme.colors.background.card,
    },
    text: {
      primary: theme.colors.text.primary,
      secondary: theme.colors.text.secondary,
    },
  },
  typography: {
    fontFamily: theme.typography.fontFamily.primary,
  },
});

function App() {
  // Application state
  const [amplifyInitialized, setAmplifyInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState(null);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  const now = dayjs();
  const currentDate = now.format("MMMM Do");
  const oldest_date = now.subtract(90, 'days').format("DD MMMM YYYY");

  
  // KPI configurations extracted from KPIContainer
  const kpiConfigs = [
    {
      key: 'total_open',
      title: 'Total Ongoing SA',
      kpiType: 'total_open',
      getValue: (data) => data?.total_open || 0,
      formatValue: (value) => value.toLocaleString(),
    },
    {
      key: 'sa_closed_today',
      title: 'SA Closed on ' + currentDate,
      kpiType: 'sa_closed_today',
      getValue: (data) => data?.total_closed_today || 0,
      formatValue: (value) => value.toLocaleString(),
    },
    {
      key: 'sa_opened_today',
      title: 'SA Opened on ' + currentDate,
      kpiType: 'sa_opened_today',
      getValue: (data) => data?.sa_opened_today || 0,
      formatValue: (value) => value.toLocaleString(),
    }
  ];

  // Initialize Amplify on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('[App] Initializing application...');
        
        // Initialize Amplify
        const amplifySuccess = initializeAmplify();
        if (!amplifySuccess) {
          console.warn('[App] Amplify initialization failed, but continuing with fallback configuration');
        }
        
        setAmplifyInitialized(true);
        console.log('[App] Application initialized successfully');
        
      } catch (error) {
        console.error('[App] Failed to initialize application:', error);
        setInitializationError(error);
        setShowErrorSnackbar(true);
        
        // Still set as initialized to allow fallback behavior
        setAmplifyInitialized(true);
      }
    };
    
    initializeApp();
  }, []);

  // Fixed refresh interval (5 minutes)
  const refreshInterval = 3600000;

  // Data hook - unified data provider
  const {
    mapData,
    kpiData,
    previousKpiData,
    countryMapData,
    cityMapData,
    storeLocationData,
    countryAggregatedData,
    summaryData,
    loading,
    error,
    lastUpdated,
    refetch,
    formatKPIValue,
    getFormattedKPI,
    getKPIChange
  } = useSharedTicketData(refreshInterval);

  // Enhanced refresh function with error handling
  const handleRefresh = async (clearCache = false) => {
    try {
      console.log(`[App] Manual refresh triggered${clearCache ? ' (clearing cache)' : ''}`);
      
      await refetch(clearCache);
      
      console.log('[App] Manual refresh completed successfully');
    } catch (error) {
      console.error('[App] Error during manual refresh:', error);
      setShowErrorSnackbar(true);
    }
  };

  // Enhanced interaction handlers
  const handleCountryHover = (countryData) => {
    // console.log('[App] Country hovered:', countryData);
  };

  const handleCountryClick = (countryData) => {
    // console.log('[App] Country clicked:', countryData);
  };

  // Error snackbar handler
  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  // Show loading screen while initializing
  if (!amplifyInitialized) {
    return (
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: theme.colors.background.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" color={theme.colors.text.secondary}>
            Initializing Service Appointments Dashboard...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: theme.colors.background.primary,
          color: theme.colors.text.primary,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          {/* Header with Logo - 60px total height */}
          <Box
            sx={{
              // p: 2,
              p: '0px 24px 0px 12px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme.colors.neutral[400]}`,
              backgroundColor: theme.colors.background.topbar,
              flexShrink: 0,
              // columnGap: '24px'
            }}
          >
            {/* Logo on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', transform: 'translateX(0%)'}}>
              <img
                src="/LOGO-EptaService_White_Ho_3D_RGB.png"
                alt="EptaService Logo"
                style={{
                  height: '55px',
                  width: 'auto',
                }}
              />
            </Box>
            
            {/* Title in the center */}
            {/* <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.text.inverse,
                fontSize: '1.3rem',
                lineHeight: 1,
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              Field Service Management Dashboard
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.75rem',
                fontStyle: 'italic',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: '40px', // Position below the main title
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              *Displaying appointments opened in current year (2026)
            </Typography> */}
            <Box sx={{ 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center'
            }}>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text.inverse,
                  fontSize: '1.3rem',
                  lineHeight: 1,
                  mb: 0.5
                }}
              >
                Field Service Management
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.7rem',
                  fontStyle: 'italic',
                  display: 'block'
                }}
              >
                Displaying appointments created since {oldest_date}
              </Typography>
            </Box>
            
            {/* Last Updated indicator on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LastUpdated
                timestamp={lastUpdated}
                onRefresh={handleRefresh}
                loading={loading}
                position="inline"
                showRelativeTime={false}
                showRefreshButton={false}
                variant="compact"
              />
            </Box>
            
            {/* Environment indicator (development only) - inline */}
            {/* {environmentInfo?.debugApi && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  ml: 2,
                  fontSize: '0.7rem',
                }}
              >
                {environmentInfo.isDevelopment || environmentInfo.forceDevMode ? 'Dev' : 'Prod'} | {environmentInfo.amplifyApiName}
              </Typography>
            )} */}
          </Box>

          {/* KPI Strip - 60px total height */}
          {/* <Box
            sx={{
              height: '60px',
              flexShrink: 0,
              backgroundColor: theme.colors.background.kpiContainer,
              backdropFilter: 'blur(10px)',
            }}
          >
            <KPIContainer
              kpiData={kpiData}
              previousKpiData={previousKpiData}
              loading={kpiLoading}
              error={kpiError}
              position="strip"
            />
          </Box> */}

          {/* Map Container - Full width */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Map takes full width */}
            <EuropeMap
              onCountryHover={handleCountryHover}
              onCountryClick={handleCountryClick}
              height="100%"
              focusOnEurope={true}
              refreshInterval={refreshInterval}
              countryMapData={countryMapData}
              cityMapData={cityMapData}
              storeLocationData={storeLocationData}
              countryAggregatedData={countryAggregatedData}
              summaryData={summaryData}
              loading={loading}
              error={error}
            />

            {/* KPI Cards as absolute overlays */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 32, // Moved down to avoid map zoom controls
                left: 16,
                width: { xs: 250, md: 250, lg: 290 },
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {loading ? (
                // Loading state
                kpiConfigs.map((config, index) => (
                  <motion.div
                    key={config.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    style={{ width: '100%' }}
                  >
                    <KPICard
                      title={config.title}
                      value={0}
                      kpiType={config.kpiType}
                      loading={true}
                      compact={false}
                    />
                  </motion.div>
                ))
              ) : error ? (
                // Error state
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    Error Loading KPIs: {error.message || 'Failed to load KPI data.'}
                  </Alert>
                </motion.div>
              ) : (
                // Normal state - render individual KPI cards
                kpiConfigs.map((config, index) => {
                  const currentValue = config.getValue(kpiData);
                  const previousValue = config.getValue(previousKpiData);
                  const subtitle = config.getSubtitle ? config.getSubtitle(kpiData) : undefined;

                  return (
                    <motion.div
                      key={config.key}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                        ease: "easeOut"
                      }}
                      style={{ width: '100%' }}
                    >
                      <KPICard
                        title={config.title}
                        value={currentValue}
                        previousValue={previousValue}
                        subtitle={subtitle}
                        kpiType={config.kpiType}
                        formatValue={config.formatValue}
                        loading={false}
                        compact={false}
                      />
                    </motion.div>
                  );
                })
              )}
            </Box>

            {/* Data Freshness Indicator */}
            {/* <LastUpdated
              timestamp={lastUpdated}
              onRefresh={handleRefresh}
              loading={loading}
              position="bottom-right"
              showRelativeTime={true}
              showRefreshButton={true}
              variant="default"
            /> */}
          </Box>
        </Box>
        
        {/* Error Snackbar */}
        <Snackbar
          open={showErrorSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseErrorSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseErrorSnackbar}
            severity="error"
            sx={{ width: '100%' }}
          >
            {initializationError?.message || 'An error occurred while loading data. Please try refreshing the page.'}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default App;
